/* eslint-disable max-len */
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { initialQueryParamsState, usePaginatedGetReceiptsQuery } from './hooks/usePaginatedGetReceiptsQuery';
import { If, Loader, NoPermissions, Pagination, SearchWithDropdown } from 'components';
import { InvoicesPageIcon, NoSearchResultsIcon, SearchIcon } from 'assets/icons';
import {
  getNotifications,
  selectCurrentTeam,
  selectCurrentTeamInvoicePermissions,
  selectMerchantTeams,
  setCurrentTeam,
  showNotifier
} from 'containers/app/appSlice';
import { constructArrayAsValue, debounce } from 'utils/commonUtils';
import isEqual from 'lodash/isEqual';
import { ReceiptColumnInfo, ReceiptSearchKey, ReceiptStatus } from './receipts.model';
import { Receipt } from './receipt.model';
import { useLazyDownloadReceiptsQuery } from './api';
import ReceiptsTable from './components/ReceiptsTable';
import ReceiptsFilter from './components/ReceiptFilters';
import ReceiptsTableActions from './components/ReceiptsTableActions';
import { ExportMenu } from 'containers/invoices/components';
import { FileTypes, clevertapEvents } from 'types/baseTypes';
import { useAppDispatch, useAppSelector } from 'hooks';
import { NotifierTypes } from 'constants/notifierConstants';
import { ReceiptRoutesState } from 'routes/types';
import DateUtils from 'utils/dateUtils';
import { searchTypes } from 'constants/common';
import { logAnalyticEvent } from 'utils/analytics';
import routesPath from 'routes/RoutesPath';
import { InfoMesssages } from 'types/infoMessages';
import { ROOT_ROUTE_STATE } from 'routes/constants';
import { resetReceiptState } from './receiptsSlice';
import { resetInvoiceState } from 'containers/invoices/InvoicesSlice';
import { PaymentSystem } from 'containers/payouts/constants';
import CustomizeColumnsModal, { ColumnConfig } from 'components/customize-columns-modal/CustomizeColumnsModal';
import { useSaveCustomColumnsMutation } from 'containers/accounting/pay-runs/api';

const ITEMS_PER_PAGE = 50;
const PAGE_SIZE_STORAGE_KEY = 'scanpay-receipt-page-size';

const getInitialPageSize = (): number => {
  try {
    const storedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    if (storedPageSize) {
      const parsed = parseInt(storedPageSize, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
  } catch (error) {
    //
  }
  return ITEMS_PER_PAGE;
};

const Receipts: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const receiptPermissions = useAppSelector(selectCurrentTeamInvoicePermissions);
  const [currentReceiptId, setCurrentReceiptId] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedReceipts, setSelectedReceipts] = useState<Receipt[]>([]);
  const [isExportPopoverOpen, setIsExportPopoverOpen] = useState(false);
  const [showEditTableModal, setShowEditTableModal] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [downloadReceiptsQuery] = useLazyDownloadReceiptsQuery();
  const [saveCustomColumns, { isLoading: isSavingColumns }] = useSaveCustomColumnsMutation();
  const merchantTeams = useAppSelector(selectMerchantTeams);
  const dispatch = useAppDispatch();
  const receiptDetailsId = searchParams.get('id');
  const teamId = searchParams.get('teamId');
  const notifications = useAppSelector(getNotifications);
  const { teamId: teamIdFromUrl } = useParams();

  const initialPageSize = getInitialPageSize();

  const {
    list: receiptsList,
    isLoading: isLoadingReceipts,
    fetchData,
    resetQuery: resetReceiptsQuery,
    setQueryParams: setReceiptsQueryParams,
    queryParams: receiptsQueryParams,
    totalListCount: receiptsCount,
    isLoadingSuccess: receiptLoadingSuccess,
    hasPermissionsToView,
    searchType,
    setSearchType,
    columnConfig,
    page
  } = usePaginatedGetReceiptsQuery(initialPageSize);

  const previousTeamIdRef = useRef<number | undefined>(currentTeam?.id);
  const routeStateAppliedRef = useRef(false);

  const isInitialQueryParamsState = () => {
    return isEqual(receiptsQueryParams, { ...initialQueryParamsState, limit: initialPageSize });
  };

  useEffect(() => {
    if (currentTeam?.id && previousTeamIdRef.current !== currentTeam?.id) {
      resetReceiptsQuery();
      setSearchText('');
      setSearchType(searchTypes[1]);
      routeStateAppliedRef.current = false;
      previousTeamIdRef.current = currentTeam?.id;
    }
  }, [currentTeam?.id, resetReceiptsQuery, setSearchType]);

  useEffect(() => {
    resetReceiptsQuery();
  }, [receiptPermissions.viewReceiptsBySelf, receiptPermissions.viewReceiptsByTeam]);

  useEffect(() => {
    const routeState = location.state as ReceiptRoutesState;

    if (routeState?.isToday && !routeStateAppliedRef.current) {
      const today = new Date();
      setReceiptsQueryParams(prevQueryParams => ({
        ...prevQueryParams,
        createdAtFrom: DateUtils.ensureUTCStartDateFormat(
          DateUtils.getDateInFormat({ date: today, dateFormat: 'yyyy-MM-dd' })
        ),
        createdAtTo: DateUtils.ensureUTCEndDateFormat(
          DateUtils.getDateInFormat({ date: today, dateFormat: 'yyyy-MM-dd' })
        ),
        status: [ReceiptStatus.SUCCESS]
      }));
      routeStateAppliedRef.current = true;
    }

    const teamIdVal = teamIdFromUrl ? teamIdFromUrl : teamId;
    if (teamIdVal && currentTeam?.id !== +teamIdVal) {
      const merchantTeam = merchantTeams.find(team => team.id === +teamIdVal);
      if (merchantTeam) {
        dispatch(setCurrentTeam(merchantTeam));
        dispatch(resetReceiptState());
        dispatch(resetInvoiceState());
      } else {
        navigate(routesPath.RECEIPTS, { replace: true });
      }
    }
  }, [location, currentTeam?.id, merchantTeams, teamIdFromUrl, teamId, dispatch, navigate, setReceiptsQueryParams]);

  useEffect(() => {
    if (receiptDetailsId && receiptLoadingSuccess) {
      navigate(routesPath.RECEIPTS, { replace: true });
      handleReceiptClick(receiptDetailsId);
    }
  }, [receiptDetailsId, receiptLoadingSuccess]);

  const handleFiltersChange = useCallback((filters: Record<string, unknown>) => {
    setReceiptsQueryParams(prevParams => ({
      ...prevParams,
      ...filters
    }));
  }, []);

  const initiateSearch = (searchTerm?: string | null) => {
    setReceiptsQueryParams(prevQueryParams => ({ ...prevQueryParams, search: searchTerm ?? null }));
  };

  const debouncedSearchHandler = useCallback(debounce(initiateSearch, 500), []);

  const onSearchTermChange = (searchTerm: string) => {
    setSearchText(searchTerm);
    debouncedSearchHandler(searchTerm);
  };

  const onClearSearch = () => {
    setSearchText('');
    initiateSearch(null);
  };

  const handlePageChange = (pageNumber: number) => {
    fetchData(pageNumber);
  };

  const handleSortChangeWrapper = (sortByParam: string, sortOrderParam: string) => {
    setReceiptsQueryParams(prevParams => ({
      ...prevParams,
      sortBy: sortByParam,
      sortOrder: sortOrderParam
    }));
  };

  const handleSelectResultsPerPage = (pageSize: number) => {
    try {
      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
    } catch (error) {
      //
    }
    setReceiptsQueryParams(prevQueryParams => ({
      ...prevQueryParams,
      limit: pageSize
    }));
  };

  const handleEditTableClick = () => {
    setShowEditTableModal(true);
  };

  const handleEditTableModalClose = () => {
    setShowEditTableModal(false);
  };

  const handleEditTableSave = async (columns: ColumnConfig[]) => {
    if (!currentTeam?.id) return;

    try {
      await saveCustomColumns({
        teamId: currentTeam.id,
        payload: {
          feature: 'RECEIPTS',
          columnInfo: columns.map(col => ({
            columnKey: col.columnKey,
            columnLabel: col.columnLabel,
            isEnabled: col.isEnabled,
            priority: col.priority,
            showAlways: col.showAlways,
            isFocused: col.isFocused
          }))
        }
      }).unwrap();

      handleEditTableModalClose();
      fetchData(page);
    } catch (error) {
      //
    }
  };

  const getReceiptsColumn = useMemo(() => {
    return columnConfig.map(col => ({
      columnKey: col.columnKey,
      columnLabel: col.columnLabel,
      isEnabled: col.isEnabled,
      priority: col.priority,
      showAlways: col.showAlways,
      isFocused: col.isFocused
    }));
  }, [columnConfig]);

  const handleSidePanelClose = () => {
    setCurrentReceiptId('');
    navigate(routesPath.RECEIPTS);
  };

  const handleReceiptClick = (receiptId: string) => {
    navigate(`/payments/receipts/${currentTeam?.id}/${receiptId}/receipt`, { state: ROOT_ROUTE_STATE });
  };

  const handleRefundTransactionClick = (receiptId: string) => {
    navigate(`/payments/receipts/${currentTeam?.id}/${receiptId}/refund`, { state: ROOT_ROUTE_STATE });
  };

  const handleDisputeTransactionClick = (disputeId: string, receiptId: string) => {
    navigate(`/payments/receipts/${currentTeam?.id}/${receiptId}/dispute/${disputeId}`, {
      state: ROOT_ROUTE_STATE
    });
  };

  const handleShareReceiptClick = (invoiceId: string, receiptId: string) => {
    navigate(`/payments/receipts/${currentTeam?.id}/${receiptId}/share-receipt/${receiptId}`, {
      state: ROOT_ROUTE_STATE
    });
  };

  const handleCreateRefundClick = (receiptId: string) => {
    navigate(`/payments/receipts/${currentTeam?.id}/${receiptId}/create-refund`, { state: ROOT_ROUTE_STATE });
  };

  const handlePayoutClick = (payoutId: string, receiptId: string, paymentSystem: PaymentSystem) => {
    const type = paymentSystem || PaymentSystem.LIP;
    navigate(`/payments/receipts/${currentTeam?.id}/${receiptId}/payout/${payoutId}?type=${type}`, {
      state: ROOT_ROUTE_STATE
    });
    setCurrentReceiptId(receiptId);
  };

  const handleExportClick = () => {
    if (isExportDisabled) {
      onInfoClick('receipts-export', InfoMesssages.noReceiptsToExport);
      return;
    }
    setIsExportPopoverOpen(prevState => !prevState);
  };

  const onCloseExportPopover = () => {
    setIsExportPopoverOpen(false);
  };

  const handleSearchIconClick = () => {
    setClicked(true);
  };

  const onDownloadFile = (fileType: FileTypes) => {
    const { search, type, amount, ...rest } = receiptsQueryParams;
    const isSearchPresent = search !== null && search !== '';
    downloadReceiptsQuery({
      teamId: currentTeam?.id,
      ...rest,
      amount: amount || null,
      limit: null,
      page: 0,
      type: fileType,
      selectedIds: selectedReceipts.map(r => r.id),
      searchKey: isSearchPresent ? (searchType.type as ReceiptSearchKey) : null,
      searchValue: isSearchPresent ? search.trim() : null
    });
    dispatch(
      showNotifier({
        message: {
          primaryMessage: 'Downloading Receipt may take few seconds',
          secondaryMessage: ''
        },
        type: NotifierTypes.INFO,
        showClose: false,
        fontStyle: 'text-primary font-normal'
      })
    );
    logAnalyticEvent(clevertapEvents.webReceiptsListExport, {
      ...constructArrayAsValue('Customers', receiptsQueryParams.customerId),
      ...constructArrayAsValue('Created by', receiptsQueryParams.createdBy),
      'Start Date': receiptsQueryParams.createdAtFrom,
      'End Date': receiptsQueryParams.createdAtTo,
      ...constructArrayAsValue('Status', receiptsQueryParams.status),
      ...constructArrayAsValue('Payment Method', receiptsQueryParams.paymentMethod),
      ...constructArrayAsValue('Type', receiptsQueryParams.sourceType),
      'Tip Collected': receiptsQueryParams.tipAmountNE
    });
    onCloseExportPopover();
  };

  const isFilterResultEmpty =
    receiptsList?.length === 0 && !isLoadingReceipts && !isInitialQueryParamsState() && !receiptsQueryParams.search;

  const isSearchResultEmpty =
    receiptsList?.length === 0 &&
    !isLoadingReceipts &&
    receiptsQueryParams.search !== null &&
    receiptsQueryParams.search !== '';

  const isReceiptResultEmpty =
    receiptsList?.length === 0 &&
    !isLoadingReceipts &&
    isInitialQueryParamsState() &&
    !receiptsQueryParams.search &&
    receiptLoadingSuccess;

  const isExportDisabled = isFilterResultEmpty || isSearchResultEmpty || receiptsList?.length === 0;

  const onInfoClick = (id: string, infoMessage: string) => {
    const existingNotification = notifications.find(notification => notification.id === id);
    if (existingNotification) return;
    dispatch(
      showNotifier({
        id,
        message: {
          primaryMessage: infoMessage,
          secondaryMessage: '',
          isMessageHtml: true
        },
        type: NotifierTypes.WARNING,
        showClose: true,
        fontStyle: 'text-primary font-normal'
      })
    );
  };

  return (
    <>
      <div className="overflow-y flex h-screen flex-col justify-between pt-6">
        <div className="mb-6 flex flex-row items-center gap-3 px-6">
          <div className="text-2xl font-bold text-primaryText">Receipts</div>
          <If condition={!clicked}>
            <div onClick={handleSearchIconClick} role="presentation">
              <SearchIcon className="mx-2 flex lg:hidden" />
            </div>
          </If>
          <SearchWithDropdown
            selectedOption={searchType}
            setSelectedOption={setSearchType}
            menuItems={searchTypes.filter(item => item.type !== 'invoiceNo')}
            searchId="receipt-search"
            onClearSearchInput={onClearSearch}
            onSearchInputChange={onSearchTermChange}
            searchInputvalue={searchText}
            wrapperStyle={`w-38 h-10 cursor-pointer ${!hasPermissionsToView ? 'pointer-events-none' : ''} 
            ${!clicked ? 'lg:flex sm:hidden' : ''} `}
            autoFocus={!!hasPermissionsToView}
          />
        </div>

        {hasPermissionsToView && (
          <div className="my-3 px-6">
            <ReceiptsFilter onFiltersChange={handleFiltersChange} />
          </div>
        )}

        {hasPermissionsToView && receiptsList?.length !== 0 && (
          <div className="flex h-10 w-full items-center border-y border-borderGray bg-[#F4F4F4] py-1 px-[30px]">
            <ReceiptsTableActions
              onExportClick={handleExportClick}
              onEditTableClick={handleEditTableClick}
              isExportPopoverOpen={isExportPopoverOpen}
              onCloseExportPopover={onCloseExportPopover}
              exportMenu={<ExportMenu id="receipts-export" onDownloadFile={onDownloadFile} />}
            />
          </div>
        )}

        <div className="mt-top customNormalScroll grow pb-5">
          <If condition={isLoadingReceipts}>
            <Loader />
          </If>

          <If condition={isReceiptResultEmpty && hasPermissionsToView}>
            <div className="flex h-full flex-col items-center justify-center gap-5">
              <div className="flex flex-col items-center gap-5">
                <InvoicesPageIcon className="h-10 w-10" />
                <div className="text-sbase text-primaryText">No receipts found</div>
              </div>
            </div>
          </If>

          <If condition={isFilterResultEmpty && hasPermissionsToView}>
            <div className="flex h-full flex-col items-center justify-center gap-5">
              <div className="flex flex-col items-center gap-5">
                <InvoicesPageIcon className="h-10 w-10" />
                <div className="text-sbase text-primaryText">No receipts found for selected filters</div>
              </div>
            </div>
          </If>

          <If condition={isSearchResultEmpty && hasPermissionsToView}>
            <div className="flex h-full flex-col items-center justify-center gap-5">
              <div className="flex flex-col items-center gap-5">
                <NoSearchResultsIcon />
                <div className="text-sbase text-primaryText">No results found!</div>
              </div>
            </div>
          </If>

          <If condition={!isLoadingReceipts && !hasPermissionsToView}>
            <div className="flex h-full flex-col items-center justify-center gap-5">
              <NoPermissions />
            </div>
          </If>

          <If condition={receiptsList?.length !== 0 && !isLoadingReceipts && hasPermissionsToView}>
            <ReceiptsTable
              receiptsList={receiptsList}
              onReceiptClick={handleReceiptClick}
              onPayoutClick={handlePayoutClick}
              currentSelectedId={currentReceiptId}
              onRefundTransactionClick={handleRefundTransactionClick}
              onShareReceiptClick={handleShareReceiptClick}
              onCreateRefundClick={handleCreateRefundClick}
              onDisputedTransactionClick={handleDisputeTransactionClick}
              columnInfo={columnConfig}
              onSortChange={handleSortChangeWrapper}
              currentSortBy={receiptsQueryParams.sortBy}
              currentSortOrder={receiptsQueryParams.sortOrder}
              stickyHorizontalScrollbar={{ bottomOffsetPx: 48 }}
              selectedReceipts={selectedReceipts}
              onSelectionChange={setSelectedReceipts}
            />
          </If>
        </div>

        <If condition={receiptsList?.length > 0 && hasPermissionsToView}>
          <Pagination
            currentPage={page}
            totalCount={receiptsCount}
            onPageChange={handlePageChange}
            limit={receiptsQueryParams.limit ?? initialPageSize}
            wrapperClassName="sticky bottom-0 z-30 w-full !h-12 py-2.5"
            showSelectResultsPerPage={true}
            onSelectResultsPerPage={handleSelectResultsPerPage}
          />
        </If>
      </div>

      <Outlet context={{ handleSidePanelClose, resetReceiptsQuery }} />

      {showEditTableModal && (
        <CustomizeColumnsModal
          open={showEditTableModal}
          onClose={handleEditTableModalClose}
          onSave={columns => handleEditTableSave(columns as ReceiptColumnInfo[])}
          isLoading={isSavingColumns}
          columns={getReceiptsColumn}
          reorderableStartIndex={1}
        />
      )}
    </>
  );
};

export default Receipts;
